"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { FaCheck, FaChevronRight } from "react-icons/fa";
import GradientButton from "@/components/ui/GradientButton";
interface OrderingAppSectionProps {
  contentPosition?: "left" | "right";
  title: React.ReactNode;
  description: string;
  features: string[];
  buttonText?: string;
  images: {
    main: string;
    secondary: string;
    mainClassName?: string;
    secondaryClassName?: string;
    circleClassName?: string;
  };
}
export default function OrderingAppSection(t0) {
  const $ = _c(40);
  const {
    contentPosition: t1,
    title,
    description,
    features,
    buttonText: t2,
    images
  } = t0;
  const contentPosition = t1 === undefined ? "left" : t1;
  const buttonText = t2 === undefined ? "Get In Touch" : t2;
  const isContentLeft = contentPosition === "left";
  const circleRef = useRef(null);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = circleRef.current;
      if (!el) {
        return;
      }
      const media = gsap.matchMedia();
      media.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
        const rotateAnim = gsap.to(el, {
          rotation: 360,
          duration: 8,
          repeat: -1,
          ease: "none"
        });
        const orbitState = {
          angle: 0
        };
        const orbitAnim = gsap.to(orbitState, {
          angle: 360,
          duration: 6,
          repeat: -1,
          ease: "none",
          onUpdate: () => {
            const rad = orbitState.angle * Math.PI / 180;
            gsap.set(el, {
              x: Math.cos(rad) * 30,
              y: Math.sin(rad) * 30
            });
          }
        });
        const visibility = new IntersectionObserver(t5 => {
          const [entry] = t5;
          const action = entry.isIntersecting ? "resume" : "pause";
          rotateAnim[action]();
          orbitAnim[action]();
        });
        visibility.observe(el);
        return () => {
          visibility.disconnect();
          rotateAnim.kill();
          orbitAnim.kill();
        };
      });
      return () => media.revert();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  const t5 = `order-2 text-center md:text-left ${isContentLeft ? "md:order-1" : "md:order-2"}`;
  let t6;
  if ($[2] !== title) {
    t6 = <h2 className="mx-auto max-w-xl text-2xl font-bold leading-tight text-[#050748] transition-colors duration-300 dark:text-white sm:text-3xl md:mx-0 lg:text-4xl">{title}</h2>;
    $[2] = title;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== description) {
    t7 = <p className="mx-auto font-open-sans mt-4 max-w-xl text-base leading-relaxed text-[#7782a6] transition-colors duration-300 dark:text-gray-300 md:mx-0">{description}</p>;
    $[4] = description;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== features) {
    t8 = features.map(_temp);
    $[6] = features;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t8) {
    t9 = <div className="mt-6 flex flex-col items-center space-y-3 md:items-start"><div className="w-full max-w-md">{t8}</div></div>;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FaChevronRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== buttonText) {
    t11 = <div className="mt-8 flex justify-center md:justify-start"><GradientButton href="/contact" variant="peach-pink">{buttonText}{t10}</GradientButton></div>;
    $[11] = buttonText;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7 || $[17] !== t9) {
    t12 = <div className={t5}>{t6}{t7}{t9}{t11}</div>;
    $[13] = t11;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = `relative order-1 mx-auto flex h-[300px] w-full max-w-[340px] items-center justify-center overflow-hidden sm:h-[420px] sm:max-w-[440px] sm:overflow-visible md:h-[620px] md:max-w-[600px] lg:h-[650px] lg:max-w-[650px] ${isContentLeft ? "md:order-2" : "md:order-1"}`;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      willChange: "transform"
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const t15 = `absolute inset-0 z-0 m-auto origin-center rounded-full pointer-events-none ${images.circleClassName}`;
  let t16;
  if ($[20] !== t15) {
    t16 = <div ref={circleRef} style={t14} className={t15} />;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const t17 = `absolute z-10 w-[68%] max-w-[240px]  md:w-[75%] md:max-w-[300px] lg:max-w-[320px] ${images.mainClassName || "left-2 top-4 md:left-[2%] md:top-[8%]"}`;
  let t18;
  if ($[22] !== images.main) {
    t18 = <Image src={images.main} alt="App Main Mockup" width={500} height={700} priority={false} className="h-auto w-full object-contain" />;
    $[22] = images.main;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t17 || $[25] !== t18) {
    t19 = <div className={t17}>{t18}</div>;
    $[24] = t17;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = `absolute z-20 w-[72%] max-w-[255px] sm:drop-shadow-2xl md:w-[78%] md:max-w-[310px] lg:max-w-[320px] ${images.secondaryClassName || "right-2 top-16 md:right-[-2%] md:top-[2%]"}`;
  let t21;
  if ($[27] !== images.secondary) {
    t21 = <Image src={images.secondary} alt="App Secondary Mockup" width={500} height={700} priority={false} className="h-auto w-full object-contain" />;
    $[27] = images.secondary;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== t20 || $[30] !== t21) {
    t22 = <div className={t20}>{t21}</div>;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t13 || $[33] !== t16 || $[34] !== t19 || $[35] !== t22) {
    t23 = <div className={t13}>{t16}{t19}{t22}</div>;
    $[32] = t13;
    $[33] = t16;
    $[34] = t19;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t12 || $[38] !== t23) {
    t24 = <section className="bg-white transition-colors duration-300 dark:bg-gray-950"><div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12"><div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">{t12}{t23}</div></div></section>;
    $[37] = t12;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  return t24;
}
function _temp(item, index) {
  return <div key={index} className="flex items-start gap-3 py-1.5 text-left"><div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#55a8f5] text-xs text-white shadow-sm dark:bg-blue-500"><FaCheck size={10} /></div><p className="text-sm font-open-sans leading-6 text-[#7782a6] transition-colors duration-300 dark:text-gray-300 sm:text-base">{item}</p></div>;
}